import * as React from 'react';
import PropTypes from 'prop-types';
import { DataGridPremium, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid-premium';
import { isEmpty, find, isEqual } from 'lodash';
import { Meteor } from 'meteor/meteor';
import { withTracker } from 'meteor/react-meteor-data';
import { Bert } from 'meteor/themeteorchef:bert';
import { Box, FormControl, InputLabel, MenuItem, Select, Stack } from '@mui/material';
import handleErrorInClient from '../../../modules/handle-error-in-client';
import Connectors from '../../../api/Connectors/Connectors';
import Sources from '../../../api/Sources/Sources';
import Loading from '../Loading';
import { renderButton } from '../MaterialMUIComponent';

import * as connectorOps from '../../../api/Connectors/connector-operations';
import * as sourceOps from '../../../api/Sources/source-operations';

const LIMIT_SEARCHING_DATA_DEFAULT = 'All';

const TableSourceComponent = ({
  sourceList,
  connectorList,
  tableSource,
  loading,
  isExternalSources,
  startConnector,
  externalSource,
  externalAttributes,
  sqlQuery,
  limitData
}) => {
  if (loading) return <Loading />;
  const [information, setInformation] = React.useState([]);
  const [sourceSelected, setSourceSelected] = React.useState(tableSource.defaultSource);
  const [columns, setColumns] = React.useState([]);
  const [isFetching, setIsFetching] = React.useState(false);

  const CustomToolbar = () => {
    const newSource = sourceList.find(item => item._id === sourceSelected);
    return (
      <GridToolbarContainer>
        <GridToolbarExport
          csvOptions={{
            fileName: 'data',
            utf8WithBom: true
          }}
          jsonOptions={{
            fileName: newSource.layerName
          }}
        />
      </GridToolbarContainer>
    );
  };

  const fetchData = async sourceID => {
    setIsFetching(true);
    try {
      const newSource = !isExternalSources
        ? sourceList.find(item => item._id === sourceID)
        : { layerName: sourceID };
      const newConnector = !isExternalSources
        ? connectorList.find(item => item._id === newSource.connectorId)
        : startConnector;

      const serverToConnection = {
        user: newConnector.user,
        ip: newConnector.ip,
        port: newConnector.port,
        databaseName: newConnector.databaseName,
        password: newConnector.password,
        schema: newConnector.schema,
        tableName: newSource.layerName
      };

      const arrayToFirstColumn = !isExternalSources ? newSource.attributes : externalAttributes;
      const columnsFirst = arrayToFirstColumn.map(col => ({
        field: col,
        headerName: col.charAt(0).toUpperCase() + col.slice(1),
        width: 150,
        ...(col === 'ID' && { width: 90 })
      }));
      setColumns(columnsFirst);
      // const limitSearching = isExternalSources ? 50 : LIMIT_DATA_DEFAULT;
      const response = await fetch(
        'https://sd1.ipsgeoprocesing.ipsilum.com/MG/utilities/upload_to_database',
        {
          method: 'POST',
          body: JSON.stringify({
            connection: serverToConnection,
            search: sqlQuery,
            items_limit: limitData
          }),
          headers: { 'Content-Type': 'application/json' }
        }
      );
      const answer = await response.json();
      if (!isEmpty(answer) && typeof answer !== 'string') setInformation(answer);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsFetching(false);
    }
  };

  const exportDataCSV = async () => {
    const source = find(sourceList, item =>
      isExternalSources
        ? isEqual(sourceOps.getId(item), sourceOps.getId(externalSource))
        : isEqual(sourceOps.getId(item), sourceOps.getId(sourceSelected))
    );
    const connector = find(connectorList, item =>
      isEqual(connectorOps.getId(item), sourceOps.getConnectorId(source))
    );
    const { user, ip, port, databaseName, password, schema } = connector;
    const serverToConnection = {
      user,
      ip,
      port,
      databaseName,
      password,
      schema,
      tableName: sourceOps.getLayerName(source)
    };
    try {
      const response = await fetch(
        'https://sd1.ipsgeoprocesing.ipsilum.com/MG/utilities/upload_to_database',
        {
          method: 'POST',
          body: JSON.stringify({
            connection: serverToConnection,
            search: sourceOps.getSQLQuery(source),
            download: true
          }),
          headers: { 'Content-Type': 'application/json' }
        }
      );

      const answer = await response.json();
      if (!isEmpty(answer)) {
        const csvFiles = answer.csv_files;
        if (!isEmpty(csvFiles)) {
          const urlData = csvFiles[0];
          fetch(urlData)
            .then(data => data.blob())
            .then(blob => {
              const link = document.createElement('a');
              link.href = URL.createObjectURL(blob);
              link.download = urlData;
              link.click();
            })
            .catch(console.error);
        }
      }
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  const importDataCSV = async path => {
    const source = find(sourceList, item =>
      isExternalSources
        ? isEqual(sourceOps.getId(item), sourceOps.getId(externalSource))
        : isEqual(sourceOps.getId(item), sourceOps.getId(sourceSelected))
    );
    const connector = find(connectorList, item =>
      isEqual(connectorOps.getId(item), sourceOps.getConnectorId(source))
    );
    const { user, ip, port, databaseName, password, schema } = connector;
    try {
      const serverToConnection = {
        user,
        ip,
        port,
        databaseName,
        password,
        schema,
        tableName: source
      };
      await fetch('https://sd1.ipsgeoprocesing.ipsilum.com/MG/isl/exec_isl', {
        method: 'POST',
        body: JSON.stringify({
          disk: 'data3',
          get_map: false,
          udf_selected: '/home/data3/master_folder/isl/repositorios/file_to_database',
          variables: {
            connection_postgres: serverToConnection,
            file: path
          },
          output_format: null,
          guid_function: 'e1e9e293-8f8b-4cc',
          alias_process: 'Subir a base de datos Pergamino',
          explanation_process: 'Subida de archivo a la base de datos de Pergamino',
          force_repeat: true
        }),
        headers: { 'Content-Type': 'application/json' }
      });
      Bert.alert('File import successfully', 'success');
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  const handleFileImportCSV = async event => {
    const file = event.target.files[0];
    event.preventDefault();

    if (!file) {
      Bert.alert('Select file', 'danger');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('disc', 'data3');

    try {
      const response = await fetch('https://sd1.d3.ipsilum.com/upload', {
        method: 'POST',
        body: formData,
        mode: 'cors',
        credentials: 'omit',
        referrerPolicy: 'no-referrer'
      });
      const answer = await response.json();
      if (answer.status === 'OK') {
        const { data } = answer;
        if (!isEmpty(data)) {
          importDataCSV(data.path);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleImportFile = () => {
    const inputElement = document.createElement('input');
    inputElement.type = 'file';
    inputElement.onchange = handleFileImportCSV;
    inputElement.click();
  };

  React.useEffect(() => {
    const newSourceSelected = !isExternalSources ? sourceSelected : externalSource;
    fetchData(newSourceSelected);
  }, [sourceSelected, externalSource]);

  return (
    <Box display="flex" flexDirection="column" gap={2} overflowy="hidden" sx={{ m: 2 }}>
      {!isExternalSources && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="connector-select-label">Source</InputLabel>
          <Select
            labelId="connector-select-label"
            id="connector-select"
            size="small"
            value={sourceSelected}
            onChange={event => setSourceSelected(event.target.value)}
            label="Source"
          >
            {sourceList.map(item => (
              <MenuItem key={item._id} value={item._id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {isFetching ? (
        <Loading />
      ) : (
        <>
          {!isEmpty(columns) && !isEmpty(information) ? (
            <Box sx={{ height: 600, width: '100%' }}>
              <DataGridPremium
                rows={information}
                columns={columns}
                getRowId={(row, index) => {
                  const id = row.ID || row.Id || row.id;
                  return `${id}-${index}`;
                }}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 5
                    }
                  }
                }}
                pageSizeOptions={[5, 10, 25]}
                checkboxSelection
                disableRowSelectionOnClick
                components={{
                  Toolbar: CustomToolbar
                }}
              />
              <Stack direction="row" display="flex" justifyContent="flex-end" mariginTop="2%">
                {renderButton({
                  key: 'import-data-db',
                  onClick: () => exportDataCSV(),
                  contentButton: 'Import data'
                })}
                {renderButton({
                  key: 'export-data-db',
                  style: { color: '#2A3F90', borderColor: '#2A3F90', marginLeft: '1%' },
                  onClick: () => handleImportFile(),
                  contentButton: 'Export data'
                })}
              </Stack>
            </Box>
          ) : (
            <div>No data available</div>
          )}
        </>
      )}
    </Box>
  );
};

export default withTracker(({ tableSource, isExternalSources }) => {
  let sourceSub = {};
  let sourceList = [];
  let connectorList = [];
  if (!isExternalSources) {
    sourceSub = Meteor.subscribe('manySources.connectors', tableSource.sources);
    sourceList = Sources.find().fetch();
    connectorList = Connectors.find().fetch();
  }

  return {
    loading: !isEmpty(sourceSub) ? !sourceSub.ready() : false,
    sourceList,
    connectorList
  };
})(TableSourceComponent);

TableSourceComponent.defaultProps = {
  sourceList: [],
  connectorList: [],
  tableSource: {},
  isExternalSources: false,
  startConnector: {},
  externalSource: '',
  externalAttributes: [],
  sqlQuery: '',
  limitData: LIMIT_SEARCHING_DATA_DEFAULT
};
TableSourceComponent.propTypes = {
  sourceList: PropTypes.array,
  connectorList: PropTypes.array,
  tableSource: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  isExternalSources: PropTypes.bool,
  startConnector: PropTypes.object,
  externalSource: PropTypes.string,
  externalAttributes: PropTypes.array,
  sqlQuery: PropTypes.string,
  limitData: PropTypes.oneOfType(PropTypes.string, PropTypes.number)
};
